import { Form } from "antd"
import { Legend, Modal, SelectInput, Text, notification } from "components/common"
import {
  useAddSalesMutation,
  useFilerStatusMutation,
  useLazyGetFractionalProjectDataQuery,
  useLazyGetFractionalProjectsQuery,
  useLazyGetPaymentMethodQuery,
} from "features/leads/api"
import { paymentsListFullyComplete } from "features/leads/components/addSalesModal/components/addSalesForm/components/paymentSection/utils/paymentUtils"
import FractionalPaymentSection from "./component/fractionalPaymentSection"
import PsidSuccessModal from "./component/psidSuccessModal"
import {
  FRACTIONAL_PAYMENT_CHANNEL_PSID,
  FRACTIONAL_PAYMENT_CHANNEL_STANDARD,
  isFractionalPsidChannelRowComplete,
  isPsidModeKey,
  resolvePsidPaymentModeKey,
} from "./component/fractionalPaymentUtils"
import { useFractionalSalePaymentApis } from "./hook/useFractionalSalePaymentApis"
import { useEffect, useMemo, useRef } from "react"
import { t } from "i18next"
import FractionalBlocksCard from "./component/fractionalBlocksCard"
import FractionalBlocksSummary from "./component/fractionalBlocksSummary"
import { ClientForm } from "features/clients/components"
import { useLazyGetClientDetailQuery } from "features/clients/detail/api"
import { useLazyGetProjectDetailFormQuery } from "features/projects/api"
import { debounce } from "lodash"
import {
  FRACTIONAL_SALE_CLIENT_REQUIRED_FIELDS,
  FRACTIONAL_SALE_DEFAULT_FORM_VALUES,
  canFetchFractionalPartnerFees,
  computeFractionalBlocksTotal,
  computeFractionalGrandTotal,
  findSelectedFractionalProject,
  isPaymentProofOcrEnabledForFractionalProject,
  isRaFlowEnabledForFractionalProject,
  partnerFeeTotalFromFeeData,
  taxTotalFromFeeData,
} from "./component/utilis/fractionalSaleCalculations"
import { FractionalSaleModalSkeleton, modalTitle, renderBlocksHeader } from "./component/utilis"

export default function FractionalSaleModal({ lead, formRef, client, from = {} }) {
  const [form] = Form.useForm()
  const clientFormRef = useRef()
  const psidSuccessModalRef = useRef()
  const [addSales, { isLoading: isAddingSale }] = useAddSalesMutation()
  const paymentApis = useFractionalSalePaymentApis()
  const [getFractionalProjects, { data: projectOptions = [], isFetching: loadingProjects, error: projectsError }] =
    useLazyGetFractionalProjectsQuery()
  const [fetchBlockData, { data: blockData, isFetching: loadingBlockData, error: blockDataError }] =
    useLazyGetFractionalProjectDataQuery()
  const [getPaymentMethod, { data: modeOfPayments = [], isFetching: loadingPaymentMethods }] =
    useLazyGetPaymentMethodQuery()
  const [fetchProjectDetail, { data: projectDetail, isFetching: loadingProjectDetail }] =
    useLazyGetProjectDetailFormQuery()
  const [getClientDetail, { data: clientData, error, isFetching }] = useLazyGetClientDetailQuery()
  const [getFilerStatus, { data: filerStatus, isFetching: loadingFilerStatus }] = useFilerStatusMutation()

  const clientId = client?.id || lead?.Client?.id
  const projectId = Form.useWatch("project", form)
  const blockCount = Form.useWatch("blockCount", form)
  const paymentsWatch = Form.useWatch("payments", form)
  const payments = Array.isArray(paymentsWatch) ? paymentsWatch : []
  const paymentModeKey = Form.useWatch(["payments", 0, "modeOfPayment"], form)
  const depositSlip = Form.useWatch(["payments", 0, "bank_deposit_slip"], form)
  const paymentProof = Form.useWatch(["payments", 0, "paymentProof"], form)
  const fractionalPaymentChannel =
    Form.useWatch("fractionalPaymentChannel", form) ?? FRACTIONAL_PAYMENT_CHANNEL_STANDARD

  const psidModeKeyResolved = resolvePsidPaymentModeKey(modeOfPayments)
  const isPsidPaymentFlow =
    fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID || isPsidModeKey(paymentModeKey, modeOfPayments)

  const blocksTotal = computeFractionalBlocksTotal(blockData, blockCount)

  const canFetchPartnerFees = canFetchFractionalPartnerFees({ clientId, projectId, blockCount })

  const feeData = paymentApis.psidFeeData
  const psidPartnerFeeTotal = partnerFeeTotalFromFeeData(feeData)
  const taxTotal = taxTotalFromFeeData(feeData)

  const grandTotal = computeFractionalGrandTotal({
    form,
    blockData,
    blocksTotal,
    taxTotal,
    partnerFeeTotal: psidPartnerFeeTotal,
    processingFee: filerStatus?.data?.processingFee,
  })

  const psidProcessingFeesGateOk =
    !canFetchPartnerFees ||
    (paymentApis.psidFeesFetchSuccess && !paymentApis.loadingPsidProcessingFees && !paymentApis.psidFeesError)

  const selectedFractionalProject = findSelectedFractionalProject(projectOptions, projectId)
  const paymentProofOcrEnabled = isPaymentProofOcrEnabledForFractionalProject(
    projectDetail,
    blockData,
    selectedFractionalProject
  )
  const isRaFlowEnabledForPayments = isRaFlowEnabledForFractionalProject(
    projectDetail,
    blockData,
    selectedFractionalProject
  )

  const loadFilerStatusAndProjects = () => {
    form.resetFields()
    form.setFieldsValue({
      project: undefined,
      ...FRACTIONAL_SALE_DEFAULT_FORM_VALUES,
    })
    getFilerStatus(client?.id || lead?.Client?.id)
      .unwrap()
      .catch(e => {
        notification.error({ message: e?.error || e?.message || t("Could not load filer status") })
      })
    getFractionalProjects()
      .unwrap()
      .catch(e => {
        notification.error({ message: e?.error || e?.message || t("Could not load fractional projects") })
      })
  }

  const onCancel = () => {
    psidSuccessModalRef.current?.setVisible(false)
    form.resetFields()
    form.setFieldsValue(FRACTIONAL_SALE_DEFAULT_FORM_VALUES)
    formRef?.current?.setVisible(false)
  }

  const onClientFormSuccess = () => {
    clientFormRef.current?.setVisible(false)
    loadFilerStatusAndProjects()
  }

  const onClientFormCancel = () => {
    clientFormRef.current?.setVisible(false)
    onCancel()
  }

  const onModalOpen = () => {
    const cid = lead?.Client?.id ?? client?.id
    if (!cid) return
    getClientDetail(cid)
      .unwrap()
      .then(res => {
        const missingFields = FRACTIONAL_SALE_CLIENT_REQUIRED_FIELDS.filter(field => !res?.[field])
        if (missingFields.length > 0) {
          clientFormRef.current?.setVisible(true)
        } else {
          loadFilerStatusAndProjects()
        }
      })
  }

  useEffect(() => {
    if (projectId) {
      form.setFieldsValue({ blockCount: 1 })
      fetchBlockData(projectId, true)
        .unwrap()
        .catch(e => {
          notification.error({ message: e?.error || e?.message || t("Could not load block data") })
        })
      const pid = +projectId
      if (pid > 0) {
        getPaymentMethod({ id: pid, flag: false })
        fetchProjectDetail(pid)
      }
    }
  }, [projectId, form, getPaymentMethod, fetchProjectDetail])

  const debouncedFractionalPartnerFees = useMemo(
    () =>
      debounce((inquiryId, projectIdNum, formInst, mutate, isPsid) => {
        const blocks = +formInst.getFieldValue("blockCount")
        if (!(blocks > 0)) return
        mutate({ inquiryId, projectId: projectIdNum, numberOfBlocks: blocks, isPsid })
          .unwrap()
          .catch(e =>
            notification.error({ message: e?.error || e?.message || t("Could not load partner fees and tax") })
          )
      }, 450),
    []
  )

  useEffect(() => {
    if (!canFetchPartnerFees || +projectId <= 0) return
    debouncedFractionalPartnerFees(clientId, +projectId, form, paymentApis.fractionalPartnerFees, isPsidPaymentFlow)
  }, [
    canFetchPartnerFees,
    clientId,
    projectId,
    blockCount,
    form,
    paymentApis.fractionalPartnerFees,
    debouncedFractionalPartnerFees,
    isPsidPaymentFlow,
  ])

  const paymentsComplete =
    fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID
      ? payments.length > 0 && isFractionalPsidChannelRowComplete(payments[0], psidModeKeyResolved)
      : paymentsListFullyComplete(payments || [])

  const onSubmit = async values => {
    const banks = paymentApis.banks ?? []
    const project = {
      id: +projectId,
      name: selectedFractionalProject?.label ?? projectDetail?.name ?? "",
    }
    const isPsid =
      values?.fractionalPaymentChannel === FRACTIONAL_PAYMENT_CHANNEL_PSID ||
      isPsidModeKey(values?.payments?.[0]?.modeOfPayment, modeOfPayments)

    addSales({
      lead,
      leadSalesInfo: undefined,
      bookingForm: false,
      banks,
      modeOfPayments,
      stateManager: { dealPrice: grandTotal },
      optionalSelections: {},
      project,
      payments: values.payments,
      numberOfBlocks: values.blockCount,
      nextTask: -1,
      cdpCommitment: 100,
    })
      .unwrap()
      .then(res => {
        if (isPsid && res?.data?.psid) {
          formRef?.current?.setVisible(false)
          form.resetFields()
          form.setFieldsValue(FRACTIONAL_SALE_DEFAULT_FORM_VALUES)
          psidSuccessModalRef.current?.setVisible(true, res?.data)
        } else {
          notification.success({ message: t("Sale submitted successfully") })
          formRef?.current?.setVisible(false)
          form.resetFields()
          form.setFieldsValue(FRACTIONAL_SALE_DEFAULT_FORM_VALUES)
        }
      })
      .catch(e => {
        notification.error({ message: e?.error || e?.message || t("Could not submit sale") })
      })
  }

  const disableSubmit =
    !blockData ||
    loadingBlockData ||
    !projectId ||
    projectsError ||
    loadingPaymentMethods ||
    !psidProcessingFeesGateOk ||
    !paymentsComplete ||
    isAddingSale ||
    !modeOfPayments?.length ||
    (["cheque", "cash", "bank_order"].includes(paymentModeKey) && !depositSlip?.url)

  return (
    <>
      <Modal
        ref={formRef}
        width={820}
        title={modalTitle(lead, client, filerStatus?.data?.isFiler)}
        okText={isPsidPaymentFlow ? t("Submit and Generate PSID") : t("Submit")}
        cancelText={t("Cancel")}
        onCancel={onCancel}
        onOk={() => form.submit()}
        onModalVisible={onModalOpen}
        disableOkButton={disableSubmit}
        loading={
          isAddingSale ||
          loadingProjects ||
          (!!projectId && (loadingBlockData || loadingPaymentMethods || loadingProjectDetail))
        }
        getContainer={false}
        destroyOnClose
        maskClosable={false}
      >
        {loadingFilerStatus || isFetching || loadingProjects ? (
          <FractionalSaleModalSkeleton />
        ) : (
          <Form
            form={form}
            layout="vertical"
            preserve={false}
            initialValues={{
              ...FRACTIONAL_SALE_DEFAULT_FORM_VALUES,
            }}
            onFinish={onSubmit}
          >
            <Legend title={t("Project Details")} />
            <Form.Item
              name="project"
              label={t("Select Project")}
              rules={[{ required: true, message: t("Please select a project") }]}
              style={{ marginTop: 8 }}
            >
              <SelectInput
                optionFilterProp="label"
                loading={loadingProjects}
                options={projectOptions}
                placeholder={t("Select a fractional project")}
              />
            </Form.Item>

            {projectId && (blockData || loadingBlockData) && (
              <>
                {renderBlocksHeader()}
                <FractionalBlocksCard
                  form={form}
                  blockData={blockData}
                  loadingBlockData={loadingBlockData}
                  blockCount={blockCount}
                  blocksTotal={blocksTotal}
                />
              </>
            )}
            {blockData && projectId && !loadingBlockData && (
              <>
                <FractionalBlocksSummary
                  form={form}
                  modeOfPayments={modeOfPayments}
                  blockCount={blockCount}
                  blocksTotal={blocksTotal}
                  taxTotal={taxTotal}
                  partnerFeeTotal={psidPartnerFeeTotal}
                  grandTotal={grandTotal}
                  feeLoading={paymentApis.loadingPsidProcessingFees}
                  isFiler={filerStatus?.data?.isFiler}
                  processingFee={filerStatus?.data?.processingFee}
                />
                <FractionalPaymentSection
                  form={form}
                  referenceAmount={grandTotal}
                  projectId={projectId}
                  modeOfPayments={modeOfPayments}
                  loadingPaymentMethods={loadingPaymentMethods}
                  paymentApis={paymentApis}
                  paymentOptions={{
                    isRaFlowEnabled: isRaFlowEnabledForPayments,
                    ocrEnabled: paymentProofOcrEnabled,
                  }}
                />
              </>
            )}
          </Form>
        )}
      </Modal>
      <ClientForm
        formRef={clientFormRef}
        client={clientData}
        onSuccess={onClientFormSuccess}
        onCancel={onClientFormCancel}
        isFieldsRequired={true}
      />
      <PsidSuccessModal ref={psidSuccessModalRef} lead={lead} client={client} isFiler={filerStatus?.data?.isFiler} />
    </>
  )
}
